import { useContext, useEffect, useState } from 'react';

// -----------------LIBRARIES IMPORTS------------------- //

// -----------------STATIC IMPORTS------------------- //
import '../../../ozoner.scss';

// -----------------COMPONENTS IMPORTS------------------- //
import { Button, Input, SideModal, Typography } from 'design_system/src';
import useProviders from 'hooks/useProviders';
import {
	updateCreditLineFinancialForm,
	updateFinancialForm,
} from 'models/financial-form/controllers/financial-form.controller';
import {
	FinancialFormTypeEnum,
	FormStatus,
} from 'models/financial-form/enums/financial-form.enums';
import { commonErrorHandler } from 'models/shared/helpers/errors';
import { getSkus } from 'models/sku/controllers/sku.controller';
import { Sku, SkuStatusEnum } from 'models/sku/interfaces/sku.interface';
import SkuSelector from 'views/sku/SkuSelector';
import { SkuVariant } from 'models/sku-variant/interfaces/sku-variant.interface';
import { ReactComponent as Moto } from 'design_system/src/static/icons/Motocycle.svg';
import { useNavigate } from 'react-router-dom';
import { variantLabel } from 'models/sku-variant/helpers/skuVariantLabel';
import { ozonerContext } from '../../OzonerContext';

export function SkuSection() {
	const { financialForm, vehicle, updateFormData } = useContext(ozonerContext);
	const navigate = useNavigate();

	const form = financialForm.form as any;
	const isCreditLine = form.type === FinancialFormTypeEnum.creditLine;
	const selectedSkuId = form.sku as string | undefined;
	const selectedSkuVariantId = form.skuVariant as string | undefined;
	const selectedProviderId = form.provider as string | undefined;
	const providerSku = form.providerSku as string | undefined;

	const [skus, setSkus] = useState<Sku[]>([]);
	const providers = useProviders();
	const [openSkuModal, setOpenSkuModal] = useState(false);

	const isFormActive =
		!financialForm.loan &&
		financialForm.form.status !== FormStatus.expired &&
		financialForm.form.status !== FormStatus.canceled;

	useEffect(() => {
		getSkus({
			status: SkuStatusEnum.ACTIVE,
			requirePricedVariant: true,
			limit: -1,
		})
			.then((result) => setSkus(result.data))
			.catch(() => {});
	}, []);

	const selectedSku = skus.find((s) => s._id === selectedSkuId);
	const selectedSkuVariant = selectedSku
		? selectedSku.variants?.find((s) => s._id === selectedSkuVariantId)
		: undefined;

	const providerOptions = providers.map((p) => ({
		value: p._id,
		label: `${p.name} (${p.code})`,
	}));

	const variantProviderId = selectedSkuVariant?.provider;
	const providerValue = variantProviderId ?? selectedProviderId ?? undefined;
	const providerDisabled = !selectedSkuVariant || !!variantProviderId;

	const handleSkuChange = (sku: Sku, skuVariant: SkuVariant) => {
		const dto: any = { sku: sku._id, skuVariant: skuVariant._id };
		if (skuVariant.provider) {
			dto.provider = skuVariant.provider;
		} else if (selectedProviderId) {
			dto.provider = selectedProviderId;
		}
		const updatePromise = isCreditLine
			? updateCreditLineFinancialForm(financialForm.form._id, dto)
			: updateFinancialForm(financialForm.form._id, dto);
		updatePromise
			.then((newForm) => {
				updateFormData(newForm);
				setOpenSkuModal(false);
			})
			.catch(commonErrorHandler);
	};

	const handleProviderChange = (e: any) => {
		const dto: any = { provider: e.target.value };
		if (selectedSkuId) dto.sku = selectedSkuId;
		const updatePromise = isCreditLine
			? updateCreditLineFinancialForm(financialForm.form._id, dto)
			: updateFinancialForm(financialForm.form._id, dto);
		updatePromise.then(updateFormData).catch(commonErrorHandler);
	};

	return (
		<div
			id="sku-soli-ozoner"
			className="p_x_sm p_b_md p_t_xl w_100_per border_1 border_b_solid border_neutral_300"
		>
			<div className="display_flex flex_col flex_gap_sm">
				{(selectedSku || isFormActive) && (
					<div className="display_flex flex_align_center flex_gap_sm">
						{selectedSku ? (
							<>
								<div className="flex_grow_1 flex_center_col">
									<Typography
										scale="small"
										weight="600"
										className="text_primary_300 text_decoration_underline"
										onClick={() => navigate(`/sku/${selectedSku.code}`)}
									>
										{selectedSku.code}
									</Typography>
									<Typography scale="xsmall" weight="400" className="m_b_xxs">
										{`${selectedSku.description}`}
									</Typography>
									{selectedSkuVariant ? (
										<Typography
											scale="xsmall"
											weight="400"
											textColor="neutral_600"
											className="text_center m_b_xxs"
										>
											{variantLabel(selectedSkuVariant, providers)}
										</Typography>
									) : (
										<Typography
											scale="xsmall"
											weight="400"
											textColor="neutral_600"
										>
											Sin variante seleccionada
										</Typography>
									)}
								</div>

								{isFormActive && !vehicle && (
									<div className="flex_grow_0">
										<Button
											scale="small"
											color="primary"
											variant="outline"
											onClick={() => setOpenSkuModal(true)}
										>
											Cambiar SKU
										</Button>
									</div>
								)}
							</>
						) : (
							<Button
								icon={<Moto style={{ stroke: 'none' }} />}
								scale="small"
								color="primary"
								onClick={() => setOpenSkuModal(true)}
							>
								Asignar SKU
							</Button>
						)}
					</div>
				)}

				{isFormActive && !vehicle && (
					<>
						<Input
							title="Proveedor"
							placeholder="Selecciona un proveedor"
							onChange={handleProviderChange}
							value={providerValue}
							type="select"
							name="provider"
							options={providerOptions}
							disabled={providerDisabled}
						/>
						{providerSku && (
							<div className="display_flex flex_gap_xs flex_align_center">
								<Typography
									scale="xxsmall"
									weight="400"
									textColor="neutral_600"
								>
									Código:
								</Typography>
								<Typography scale="small" weight="600">
									{providerSku}
								</Typography>
							</div>
						)}
					</>
				)}
			</div>

			{openSkuModal && (
				<SideModal open={openSkuModal} setOpen={setOpenSkuModal}>
					<div className="pos_relative h_100_per">
						<SkuSelector
							skus={skus}
							onClick={handleSkuChange}
							selectedSkuId={selectedSkuId}
							selectedVariantId={selectedSkuVariantId}
							loading={skus.length === 0}
						/>
					</div>
				</SideModal>
			)}
		</div>
	);
}
